<script setup lang="ts">
import { ref, watchEffect } from 'vue';

interface Props {
  cardKey: string;
  imageUrl: string;
  backImageUrl: string;
  x?: number;
  y?: number;
  rotation?: number;
  relative?: boolean;
  flipped?: boolean;
  disabled?: boolean;
  idle?: boolean;
  idleImagesURLs?: string[];
  flipDuration?: string;
  name?: string;
  /** The card's name for screen readers (the face's alt, once it's turned up). Without it
   *  the face is decorative — callers usually show the name on a tag next to the card. */
  label?: string;
  loading?: 'lazy' | 'eager';
}

const props = withDefaults(defineProps<Props>(), {
  x: 0,
  y: 0,
  rotation: 0,
  relative: false,
  flipped: false,
  disabled: false,
  idle: false,
  idleImagesURLs: () => [],
  flipDuration: '0.8s',
  name: undefined,
  label: undefined,
  loading: undefined,
});

const isFlipped = ref(props.flipped);
const currentImageIndex = ref(0);

// Expose flip function to parent
defineExpose({
  flip: () => {
    isFlipped.value = !isFlipped.value;
    return isFlipped.value;
  },
});

const emit = defineEmits<{
  (e: 'click'): void;
}>();

// Idle: every 2 s the card shows the next image (a flip). The interval (and its pending
// flip) are cleared when idle ends or the card unmounts — watchEffect ignores a returned
// function, so the cleanup must go through onCleanup.
watchEffect(onCleanup => {
  if (props.idle && props.idleImagesURLs.length > 0) {
    let flipTimer: ReturnType<typeof setTimeout> | undefined;
    const interval = setInterval(() => {
      // Change the image first (while card is currently showing)
      currentImageIndex.value = (currentImageIndex.value + 1) % props.idleImagesURLs.length;

      // Then start the flip animation to reveal the new image
      // Small delay ensures the image change is applied before flip starts
      flipTimer = setTimeout(() => {
        isFlipped.value = !isFlipped.value;
      }, 10);
    }, 2000);

    onCleanup(() => {
      clearInterval(interval);
      clearTimeout(flipTimer);
    });
  }
});
</script>

<template>
  <!-- The back is decorative; the face is only named once it's turned up (a screen reader
       mustn't announce the card before the reveal). -->
  <div class="card-container">
    <div
      class="card"
      :class="{
        flipped: isFlipped,
        'position-relative': relative,
        disabled: disabled,
        idle: idle,
      }"
      :style="{
        transform: `translate(${x}px, ${y}px) rotate(${rotation}deg)`,
        '--flip-duration': props.flipDuration,
      }"
      @click="emit('click')"
    >
      <div class="card-back">
        <img
          :src="backImageUrl"
          alt=""
          width="550"
          height="966"
          :loading="loading"
        />
      </div>
      <div class="card-front">
        <img
          :src="idle && idleImagesURLs.length > 0 ? idleImagesURLs[currentImageIndex] : imageUrl"
          :alt="isFlipped ? (label ?? name ?? '') : ''"
          width="550"
          height="966"
          :loading="loading"
        />
      </div>
    </div>

    <!-- Card Name -->
    <p
      v-if="name"
      class="card-name text-sm text-muted-foreground font-medium mt-2 mb-2 text-center"
    >
      {{ name }}
    </p>
  </div>
</template>

<style scoped>
.card-container {
  aspect-ratio: 550/966 !important;
}

.card {
  position: absolute;
  width: 100%;
  height: 100%;
  perspective: 5000px;
  cursor: pointer;
  transition: transform 0.3s ease-out;
}

.card.disabled {
  cursor: not-allowed;
}

.card.position-relative {
  position: relative;
}

/* Each face is a sticker: the card art with a cream die-cut margin around it
   (a spread box-shadow ring, so the art keeps its exact aspect ratio) and a soft
   shadow lifting it off whatever it sits on. `--sticker-w` scales the margin with
   the card size (set it larger on big cards). The ring lives on the faces (they
   rotate with the flip); never a `filter` on .card or its ancestors. The flip
   eases in and out — paper turns over, it doesn't bounce. */
.card-back,
.card-front {
  position: absolute;
  width: 100%;
  height: 100%;
  backface-visibility: hidden;
  border-radius: 7px;
  box-shadow:
    0 0 0 var(--sticker-w, 3px) var(--paper-stock),
    0 calc(var(--sticker-w, 3px) + 5px) 14px -4px var(--paper-shade),
    0 1px 3px var(--paper-shade);
  transition:
    transform var(--flip-duration) cubic-bezier(0.45, 0, 0.2, 1),
    box-shadow 0.3s ease-out;
}

.card-back {
  transform: rotateY(0deg);
}

.card-front {
  transform: rotateY(-180deg);
}

.card.flipped .card-back {
  transform: rotateY(180deg);
}

.card.flipped .card-front {
  transform: rotateY(0deg);
}

.card-back img,
.card-front img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 5px;
}

.card.idle {
  cursor: default;
}

/* Reduced motion: the card just shows its other face. */
@media (prefers-reduced-motion: reduce) {
  .card,
  .card-back,
  .card-front {
    transition: none;
  }
}
</style>
